<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="labelPrintModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="labelPrintModalTitle">Labeldruck</h5>
        </div>

        <div class="modal-body">
          <div v-if="state === 'printing'" class="print-status">
            <div class="spinner" aria-label="Druckt"></div>
            <span>Das Label wird gedruckt…</span>
          </div>
          <template v-else>
            <div class="error">{{ errorMessage }}</div>
            <p class="printer-hint">
              Kein Drucker eingerichtet oder falscher Drucker gewählt?
              <a href="#" @click.prevent="onOpenPrinter">Zu „Drucker"</a>
            </p>
          </template>
        </div>

        <div v-if="state === 'error'" class="modal-footer">
          <button type="button" class="btn btn-primary" @click="onOk">OK</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';
import { printLabel } from '../services/tickets';
import { getPeppiPrinterUrl } from '../services/me';

/**
 * Druck-Modal für das Reparatur-Ticket-Label.
 * Aufruf via `print(ticketId)`: zeigt während des Druckauftrags einen
 * „wird gedruckt…"-Hinweis. Bei Erfolg verschwindet das Modal, bei einem
 * Fehler erscheint die Meldung, die mit OK zu bestätigen ist.
 */
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;

const state        = ref<'printing' | 'error'>('printing');
const errorMessage = ref<string>('');

// Bootstrap verwirft ein hide(), das während der Show-Transition kommt. Bei
// schnellem Druck-ACK (< Fade-Dauer) bliebe das Modal sonst offen. Daher: hide
// nur, wenn „shown" schon durch ist — sonst vormerken und in onShown nachholen.
let isShown  = false;
let wantHide = false;

function onShown(): void {
  isShown = true;
  if (wantHide) {
    wantHide = false;
    modal?.hide();
  }
}

function onHidden(): void {
  isShown = false;
}

function requestHide(): void {
  if (isShown) modal?.hide();
  else wantHide = true;
}

onMounted(() => {
  if (modalEl.value) {
    // Während des Drucks nicht wegklickbar (kein Backdrop-Close, kein ESC).
    modal = new Modal(modalEl.value, { backdrop: 'static', keyboard: false });
    modalEl.value.addEventListener('shown.bs.modal', onShown);
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  modalEl.value?.removeEventListener('shown.bs.modal', onShown);
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

async function print(ticketId: number): Promise<void> {
  state.value        = 'printing';
  errorMessage.value = '';
  isShown            = false;
  wantHide           = false;
  modal?.show();
  try {
    await printLabel(ticketId);
    requestHide();
  } catch (e) {
    errorMessage.value = e instanceof Error ? e.message : 'Der Druck ist fehlgeschlagen.';
    state.value        = 'error';
  }
}

function onOk(): void {
  modal?.hide();
}

/**
 * Öffnet die Drucker-Verwaltung im Solo in einem neuen Tab — gleiche Logik wie
 * der Menüpunkt „Drucker" in der TopNav. Das Fenster wird synchron im Klick
 * geöffnet (sonst greift der Popup-Blocker) und erst danach mit der
 * token-authentifizierten URL befüllt.
 */
async function onOpenPrinter(): Promise<void> {
  const win = window.open('', '_blank');
  try {
    const url = await getPeppiPrinterUrl();
    if (win) win.location.href = url;
    else window.open(url, '_blank');
  } catch (e) {
    win?.close();
    window.alert(e instanceof Error ? e.message : 'Der Drucker-Link konnte nicht geladen werden.');
  }
}

defineExpose({ print });
</script>

<style scoped>
.print-status {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 15px;
}

.printer-hint {
  margin: 14px 0 0;
  font-size: 13px;
  color: #6c757d;
}
</style>
